import React, { Component, type ComponentType, type ReactNode, type ErrorInfo } from 'react';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import type { LabelStack } from '@atlaskit/react-ufo/interaction-context';
import { useInteractionContext } from '@atlaskit/react-ufo/use-interaction-context';
import { fg } from '@atlassian/jira-feature-gating';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge';
import { fireErrorAnalytics, VIEW_WHITELIST, isViewSupported } from './fireErrorAnalytics.tsx';

interface Props extends WithAnalyticsEventsProps {
	// We are overloading `attributes` with a callback. For backwards compatibility we don't want to change the prop name

	attributes?: AnalyticsAttributes | ((arg1: Error) => AnalyticsAttributes);
	children: ReactNode;
	// this prop is coming from Atlaskit, can't rename it
	createAnalyticsEvent: NonNullable<WithAnalyticsEventsProps['createAnalyticsEvent']>;
	id: string;
	packageName?: string;
	teamName?: string;
	sendToPrivacyUnsafeSplunk?: boolean;
	ufoLabelStack?: LabelStack;
	fallbackType?: string;
}

type State = {
	analyticsEvent: UIAnalyticsEvent | null;
};

// exported for testing
export const getSupportedView = (labelStack?: LabelStack | null): string | undefined => {
	if (fg('jserrorboundary_analytics_update')) {
		return labelStack?.find((label) => isViewSupported(label.name))?.name;
	}
	const view = labelStack?.find((label) => VIEW_WHITELIST.has(label.name));
	return view?.name;
};

class CatchAndReportErrors extends Component<Props, State> {
	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const {
			packageName,
			teamName,
			id,
			attributes,
			createAnalyticsEvent,
			sendToPrivacyUnsafeSplunk,
			ufoLabelStack,
			fallbackType,
		} = this.props;
		const analyticsEvent = createAnalyticsEvent({ action: 'failed' });

		fireErrorAnalytics({
			event: analyticsEvent,
			errorInfo,
			error,
			meta: {
				id,
				packageName,
				teamName,
			},
			attributes: typeof attributes === 'function' ? attributes(error) : attributes,
			sendToPrivacyUnsafeSplunk,
			view: getSupportedView(ufoLabelStack),
			fallbackType: fg('jserrorboundary_analytics_update') ? fallbackType : undefined,
		});

		throw error;
	}

	render() {
		return this.props.children;
	}
}

type ReportErrorsType = ComponentType<
	Flow.Diff<
		Props,
		{
			createAnalyticsEvent: Props['createAnalyticsEvent'];
		}
	>
>;

export const ReportErrors: ReportErrorsType = (props) => {
	const interactionContext = useInteractionContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return (
		<CatchAndReportErrors
			{...props}
			createAnalyticsEvent={createAnalyticsEvent}
			ufoLabelStack={interactionContext?.labelStack}
		/>
	);
};
